import { useState, useEffect, useCallback } from 'react';
import StateService from '../services/StateService';
import './ReminderSetting.css';

const apiBase = process.env.REACT_APP_API_URL || '';

function base64urlToUint8Array(base64url) {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}

function ReminderSetting() {
  const [permission, setPermission] = useState(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [reminderTime, setReminderTime] = useState(
    () => StateService.getReminderTime() || ''
  );
  const [showSetting, setShowSetting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Local fallback timer for when the app/browser tab is open
  const scheduleLocalFallback = useCallback((time) => {
    if (!time || permission !== 'granted') return;

    if (window._positiveReminderTimer) {
      clearTimeout(window._positiveReminderTimer);
    }

    const [hours, minutes] = time.split(':').map(Number);
    const now = new Date();
    const target = new Date();
    target.setHours(hours, minutes, 0, 0);

    if (target <= now) {
      target.setDate(target.getDate() + 1);
    }

    const delay = target - now;
    window._positiveReminderTimer = setTimeout(() => {
      if (!StateService.isTodayCompleted()) {
        new Notification('Positive', {
          body: "Time for today's reflection prompts",
          icon: '/icon-192.png',
          tag: 'daily-reminder',
        });
      }
      scheduleLocalFallback(time);
    }, delay);
  }, [permission]);

  useEffect(() => {
    if (reminderTime && permission === 'granted') {
      scheduleLocalFallback(reminderTime);
    }
    return () => {
      if (window._positiveReminderTimer) {
        clearTimeout(window._positiveReminderTimer);
      }
    };
  }, [reminderTime, permission, scheduleLocalFallback]);

  // Subscribe to Web Push and register with the server
  const subscribeToPush = async (time) => {
    try {
      const registration = await navigator.serviceWorker.ready;

      // Fetch the VAPID public key from the server
      const keyResponse = await fetch(`${apiBase}/push/vapid-key`);
      if (!keyResponse.ok) return;
      const { publicKey } = await keyResponse.json();
      const applicationServerKey = base64urlToUint8Array(publicKey);

      // Get or create push subscription
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      }

      // Send subscription + reminder config to server
      const subJson = subscription.toJSON();
      await fetch(`${apiBase}/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          p256dh: subJson.keys.p256dh,
          auth: subJson.keys.auth,
          reminder_time: time,
          timezone_offset: new Date().getTimezoneOffset(),
        }),
      });
    } catch (e) {
      console.error('Push subscription failed:', e);
    }
  };

  // Unsubscribe from push and remove from server
  const unsubscribeFromPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();
        await fetch(`${apiBase}/push/unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint }),
        });
      }
    } catch (e) {
      console.error('Push unsubscribe failed:', e);
    }
  };

  const requestPermission = async () => {
    if (!('Notification' in window)) return 'denied';
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  };

  const handleTimeChange = async (e) => {
    const time = e.target.value;

    if (permission !== 'granted') {
      const result = await requestPermission();
      if (result !== 'granted') return;
    }

    setSaving(true);
    setReminderTime(time);
    StateService.setReminderTime(time);

    if (time) {
      scheduleLocalFallback(time);
      await subscribeToPush(time);
    }
    setSaving(false);
  };

  const handleClear = async () => {
    setSaving(true);
    setReminderTime('');
    StateService.setReminderTime(null);
    if (window._positiveReminderTimer) {
      clearTimeout(window._positiveReminderTimer);
    }
    await unsubscribeFromPush();
    setSaving(false);
  };

  // Don't render if notifications or service workers aren't supported
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return null;

  return (
    <div className="reminder-setting">
      <button
        className="reminder-toggle"
        onClick={() => setShowSetting(!showSetting)}
        aria-label="Set daily reminder"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {reminderTime && <span className="reminder-dot" />}
      </button>
      {showSetting && (
        <div className="reminder-popover">
          <p className="reminder-label">Daily reminder</p>
          {permission === 'denied' ? (
            <p className="reminder-blocked">
              Notifications are blocked. Enable them in your browser settings.
            </p>
          ) : (
            <div className="reminder-controls">
              <input
                type="time"
                value={reminderTime}
                onChange={handleTimeChange}
                className="reminder-input"
                disabled={saving}
              />
              {reminderTime && (
                <button className="reminder-clear" onClick={handleClear} disabled={saving}>
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ReminderSetting;
